import React, { useEffect, useMemo, useState } from 'react';
import { Source, Layer, Popup, useMap } from 'react-map-gl/maplibre';
import Table from 'react-bootstrap/Table';

import { useIsAdmin } from '../auth/useIsAdmin';
import { useAdminSession } from '../auth/useAdminSession';
import { useTopLayers } from '../map-gl/useTopLayers';
import {
  ERR_BAND_COLOURS, PLATES_AVAILABLE, PLATES_BEFORE_ID, PLATES_MAX_MOUNTED, PLATES_MIN_ZOOM,
  PLATE_LAYERS, boundsIntersect, buildPlateSource, compareDrawIn, errBand, errOf, featureBounds,
  featureContains, formatErr, labelAnchor, loadPlateIndex, orderedByHand, padBounds, plateDrawn,
  plateKey, plateOpacity, selectMounted, usePlatesState, PLATES_NEED_SESSION,
} from './platesConfig';

/** A MapLibre-safe id for one plate's source and layer. */
const plateLayerId = (key) => `plate-${key.replace(/[^A-Za-z0-9_-]/g, '_')}`;

/** The footprints of the plates being drawn: an outline each, and the error label. */
const FOOTPRINT_SOURCE = 'plates-footprints';
const OUTLINE_LAYER = 'plates-footprint-line';
const LABEL_LAYER = 'plates-err-label';
// Stable reference, as useTopLayers requires.
const LABEL_ORDER = [LABEL_LAYER];

/** Fraction of the view added on each side before asking which plates are in it. */
const VIEW_MARGIN = 0.15;
const MOVE_DEBOUNCE_MS = 200;

const bandColour = ['match', ['get', 'band'],
  'good', ERR_BAND_COLOURS.good,
  'fair', ERR_BAND_COLOURS.fair,
  'poor', ERR_BAND_COLOURS.poor,
  ERR_BAND_COLOURS.unknown];

const OUTLINE_PAINT = { 'line-color': bandColour, 'line-width': 1, 'line-opacity': 0.8 };
const LABEL_LAYOUT = {
  'text-field': ['get', 'label'],
  'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
  'text-size': 12,
  // Inside the sheet, just below and right of its top-left corner.
  'text-anchor': 'top-left',
  'text-offset': [0.4, 0.3],
  // Always shown, never pushing another overlay's labels off the map.
  'text-allow-overlap': true,
  'text-ignore-placement': true,
  // Better-placed plates' labels drawn over worse ones where corners meet.
  'symbol-sort-key': ['get', 'sort'],
};
const LABEL_PAINT = {
  'text-color': bandColour,
  'text-halo-color': '#ffffff',
  'text-halo-width': 1.6,
  'text-halo-blur': 0.3,
};

const readView = (map) => {
  const b = map.getBounds();
  return { bounds: [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()], zoom: map.getZoom() };
};

/**
 * Georeferenced map plates (local only) — see platesConfig.js for what and why.
 *
 * Rasters pinned under anchor-plates-top: above every base map and the DEA /
 * ASTER interpretive rasters, below Land Change and every vector overlay.
 *
 * Scale: the indexes hold thousands of plates, so a raster source is mounted
 * only for plates that are shown and in (or near) the view, from zoom
 * PLATES_MIN_ZOOM, at most PLATES_MAX_MOUNTED at once. Mounted sources are an
 * LRU (selectMounted): panning away and back refetches nothing, and a plate
 * hidden in the list stays mounted with visibility 'none' until evicted.
 *
 * Draw order: each raster is inserted directly under the one drawn above it
 * (beforeId chain), the topmost under the footprint outline, which sits under
 * anchor-plates-top. A plate mounted later therefore still lands at its place
 * in the stack; react-map-gl moves a layer when its beforeId changes.
 *
 * The error label of each drawn plate is kept above everything (useTopLayers,
 * rank 5: under the block grid, OCR gold and prospect reports) so it stays
 * legible over the raster and whatever vectors cover it.
 *
 * Owns its click handler, like the OCR gold overlay. A click inside a plate's
 * footprint identifies it, unless the click landed on a point feature (a soil
 * sample, drill hole, MINEDEX site...) - those have their own popups, and a
 * plate usually covers tens of km², so it would otherwise answer every click.
 */
export function PlatesOverlay({ overlayVisibility }) {
  const { current: map } = useMap();
  const plates = usePlatesState();
  const [picked, setPicked] = useState(null);
  const [view, setView] = useState(null);
  const [mounted, setMounted] = useState([]);
  // Internal working material, like the prospect reports: admin only.
  const isAdmin = useIsAdmin();

  // A restricted source (A-number, through the API) waits for the session.
  const session = useAdminSession(PLATES_NEED_SESSION);
  const on = PLATES_AVAILABLE && isAdmin
    ? PLATE_LAYERS.filter((p) => overlayVisibility?.[p.key] && (!p.restricted || session))
    : [];
  const onKey = on.map((p) => p.key).join(',');

  useEffect(() => { on.forEach(loadPlateIndex); }, [onKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // The view, on moveend, debounced: a fling fires several.
  useEffect(() => {
    if (!map || !onKey) return undefined;
    let timer = 0;
    const update = () => {
      clearTimeout(timer);
      timer = setTimeout(() => setView(readView(map)), MOVE_DEBOUNCE_MS);
    };
    setView(readView(map));
    map.on('moveend', update);
    return () => { clearTimeout(timer); map.off('moveend', update); };
  }, [map, onKey]);

  // Every plate of the layers that are on, by key, with whether it is shown.
  const entries = useMemo(() => {
    const out = new Map();
    PLATE_LAYERS.filter((p) => onKey.split(',').includes(p.key)).forEach((layer) => {
      if (plates.index[layer.key]?.status !== 'ok') return;
      plates.index[layer.key].features.forEach((feature) => {
        out.set(plateKey(layer, feature.properties), {
          // Drawn = shown in the list AND inside the layer's date filter.
          layer, feature, visible: plateDrawn(plates, layer, feature),
        });
      });
    });
    return out;
  }, [onKey, plates]);

  // Bottom-to-top comparison honouring the user's hand stacking (plate list).
  const cmp = useMemo(() => compareDrawIn(plates), [plates]);

  // The plates to draw now: shown and in the padded view, zoomed in far
  // enough. When there are more than can be mounted, the ones kept are those
  // the user stacked by hand (top of their stack first), then the best error
  // bound.
  const wanted = useMemo(() => {
    if (!view || view.zoom < PLATES_MIN_ZOOM) return null;
    const box = padBounds(view.bounds, VIEW_MARGIN);
    return [...entries.entries()]
      .filter(([, e]) => e.visible && boundsIntersect(featureBounds(e.feature), box))
      .sort(([ka, a], [kb, b]) => (Number(orderedByHand(plates, kb)) - Number(orderedByHand(plates, ka)))
        || (orderedByHand(plates, ka) && cmp(b.layer, b.feature.properties, a.layer, a.feature.properties))
        || ((errOf(a.feature.properties) ?? Infinity) - (errOf(b.feature.properties) ?? Infinity))
        || cmp(b.layer, b.feature.properties, a.layer, a.feature.properties))
      .slice(0, PLATES_MAX_MOUNTED)
      .map(([k]) => k);
  }, [entries, view, plates, cmp]);
  const wantedKey = wanted ? wanted.join('|') : null;

  // Zoomed out past the gate, the LRU is left as it is: the rasters stop
  // drawing (minzoom) and are back, without refetching, on zooming in.
  useEffect(() => {
    setMounted((prev) => selectMounted(prev, wanted || [], PLATES_MAX_MOUNTED, (k) => entries.has(k)));
  }, [wantedKey, entries]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mounted plates top to bottom, each with the layer it is inserted under.
  const stack = useMemo(() => {
    const list = mounted.map((k) => [k, entries.get(k)]).filter(([, e]) => e)
      .sort(([, a], [, b]) => cmp(b.layer, b.feature.properties, a.layer, a.feature.properties));
    return list.map(([key, e], i) => ({
      key, ...e, id: plateLayerId(key), beforeId: i === 0 ? OUTLINE_LAYER : plateLayerId(list[i - 1][0]),
    }));
  }, [mounted, entries, cmp]);

  // Outline and ±N m label for each plate being drawn.
  const footprints = useMemo(() => ({
    type: 'FeatureCollection',
    features: (wanted || []).map((k) => entries.get(k)).filter(Boolean).flatMap(({ layer, feature }) => {
      const err = errOf(feature.properties);
      const anchor = labelAnchor(feature);
      const props = {
        band: errBand(err), label: formatErr(err), sort: -(err ?? 1e7), layer: layer.key,
      };
      return [
        { type: 'Feature', geometry: feature.geometry, properties: { ...props, kind: 'outline' } },
        ...(anchor ? [{
          type: 'Feature', geometry: { type: 'Point', coordinates: anchor }, properties: { ...props, kind: 'label' },
        }] : []),
      ];
    }),
  // Keyed on the plates drawn, not on every prefs change: an opacity drag
  // would otherwise hand the source new data on each step.
  }), [wantedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useTopLayers('plates-err', 5, LABEL_ORDER, map, !!onKey);

  useEffect(() => {
    if (!map || !onKey) return undefined;
    const onClick = (e) => {
      const pointHit = map.queryRenderedFeatures(e.point)
        .some((f) => f.layer.id !== LABEL_LAYER && (f.layer.type === 'circle' || f.layer.type === 'symbol'));
      if (pointHit) return;
      const { lng, lat } = e.lngLat;
      const hits = [...entries.values()]
        // A plate hidden in the plate list is not on the map to be clicked.
        .filter((x) => x.visible && featureContains(x.feature, lng, lat))
        // Topmost first: the plate drawn on top is the one being looked at.
        .sort((a, b) => cmp(b.layer, b.feature.properties, a.layer, a.feature.properties))
        .map((x) => ({ plate: x.layer, props: x.feature.properties }));
      setPicked(hits.length ? { lngLat: e.lngLat, hits } : null);
    };
    map.on('click', onClick);
    return () => { map.off('click', onClick); };
  }, [map, onKey, entries, cmp]);

  useEffect(() => { if (!onKey) setPicked(null); }, [onKey]);

  if (!on.length) return null;

  return (
    <>
      {/* Declared before the rasters: the topmost one is inserted under the
          outline, which must already exist. */}
      <Source id={FOOTPRINT_SOURCE} type='geojson' data={footprints}>
        <Layer id={OUTLINE_LAYER} source={FOOTPRINT_SOURCE} type='line' beforeId={PLATES_BEFORE_ID}
               minzoom={PLATES_MIN_ZOOM}
               filter={['==', ['get', 'kind'], 'outline']} paint={OUTLINE_PAINT} />
        <Layer id={LABEL_LAYER} source={FOOTPRINT_SOURCE} type='symbol' minzoom={PLATES_MIN_ZOOM}
               filter={['==', ['get', 'kind'], 'label']} layout={LABEL_LAYOUT} paint={LABEL_PAINT} />
      </Source>
      {/* Top of the stack first, so each raster's beforeId target (the plate
          above it) exists by the time it is added. */}
      {stack.map(({ key, id, layer, feature, visible, beforeId }) => (
        <Source key={key} id={id} {...buildPlateSource(layer, feature)}>
          <Layer
            id={id}
            source={id}
            type='raster'
            beforeId={beforeId}
            minzoom={PLATES_MIN_ZOOM}
            layout={{ visibility: visible ? 'visible' : 'none' }}
            paint={{
              'raster-opacity': plateOpacity(plates, key),
              // Line-art sheets: interpolating invents colours no contour band has.
              'raster-resampling': 'nearest',
            }}
          />
        </Source>
      ))}
      {picked && (
        <Popup longitude={picked.lngLat.lng} latitude={picked.lngLat.lat}
               anchor="top" closeOnClick={false} maxWidth="340px"
               onClose={() => setPicked(null)}>
          {picked.hits.map(({ plate, props }, i) => {
            const d = plate.describe(props);
            return (
              <div key={`${plate.key}-${i}`} style={{ fontSize: 12 }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>{d.heading}</div>
                <Table striped bordered size="sm" className="mb-1">
                  <tbody>
                    {d.rows.filter(([, v]) => v).map(([k, v]) => (
                      <tr key={k}><td>{k}:</td><td>{v}</td></tr>
                    ))}
                  </tbody>
                </Table>
                {d.link && (
                  <a target="_blank" rel="noreferrer" href={d.link.href}>{d.link.text}</a>
                )}
              </div>
            );
          })}
        </Popup>
      )}
    </>
  );
}
